import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { JUNGNA_RPC, JUNGNA_TABLES } from "../lib/jungnaTables";
import ProductHeader from "../components/ProductDetails/ProductHeader";
import ImageCarousel from "../components/ProductDetails/ImageCarousel";
import SellerInfo from "../components/ProductDetails/SellerInfo";
import ProductContent from "../components/ProductDetails/ProductContent";

// 상품 데이터 타입 정의
interface Product {
  id: string;
  seller_id: string; 
  title: string;
  description: string;
  price: string;
  top_category: string;
  bottom_category: string;
  status: string;
  content: string;
  views: number;
  date: string;
  image_url: string;
  created_at: string;
}

interface Seller {
  id: string;
  name: string;
  contact: string;
  bank_name: string;
  bank_account: string;
  positive_reviews: number;
  neutral_reviews: number;
  negative_reviews: number;
}

// Supabase 쿼리 결과 타입을 위한 인터페이스 정의
interface URLMappingWithDetails {
  id: string;
  url: string;
  product_id: string;
  seller_id: string;
  bank_id: string;
  is_active: boolean;
  created_at: string;
  products: Product | null; 
}

const ProductDetails: React.FC = () => {
  console.log("--- ProductDetails 컴포넌트 렌더링 시작 ---"); // 렌더링 확인 로그

  const [product, setProduct] = useState<Product | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [seller, setSeller] = useState<Seller | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { url } = useParams<{ url: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    console.log("--- ProductDetails useEffect 시작 ---"); // useEffect 실행 확인 로그

    const fetchData = async () => {
      if (!url) {
        console.error("URL 파라미터가 없습니다.");
        navigate("/404");
        return;
      }
      setIsLoading(true);
      console.log("--- fetchData 시작 --- URL:", url); // 디버깅 로그

      try {
        // URL 매핑에서 product_id와 연관된 상품 정보를 한 번에 가져오기
        // 명시적 타입 지정
        const { data: mappingData, error: mappingError } = await supabase
          .from(JUNGNA_TABLES.urlMappings)
          .select(
            `
            *,
            products:${JUNGNA_TABLES.products}!jungna_url_mappings_product_id_fkey (*)
          `
          )
          .eq("url", `/${url}`)
          .eq("is_active", true)
          .single<URLMappingWithDetails>();

        console.log("1. mappingData:", mappingData); // 디버깅 로그
        console.log("   mappingError:", mappingError);

        if (mappingError) {
          console.error("URL 매핑 로드 실패:", mappingError);
          navigate("/404");
          return;
        }

        // 타입 가드 수정: mappingData와 mappingData.products가 null이 아닌지 확인
        if (!mappingData || !mappingData.products) {
          console.error("상품 정보가 유효하지 않습니다");
          navigate("/404");
          return;
        }

        // 이제 mappingData.products 접근이 안전함
        setProduct(mappingData.products);
        // 추가: 멀티 이미지 로드
        try {
          const { data: imgs } = await supabase
            .from(JUNGNA_TABLES.productImages)
            .select("public_url, position")
            .eq("product_id", mappingData.products.id)
            .order("position", { ascending: true });
          const urls = (imgs || [])
            .map((i: any) => i.public_url)
            .filter((u: string | null) => !!u) as string[];
          setImages(urls);
        } catch (e) {
          setImages([]);
        }
        console.log("2. product 상태 설정됨:", mappingData.products); // 디버깅 로그

        // 판매자 정보 가져오기
        console.log("3. mappingData.seller_id:", mappingData.seller_id); // 디버깅 로그
        if (mappingData.seller_id) {
          const { data: sellerData, error: sellerError } = await supabase
            .from(JUNGNA_TABLES.sellers)
            .select("*")
            .eq("id", mappingData.seller_id)
            .single();

          console.log("4. sellerData:", sellerData); // 디버깅 로그
          console.log("   sellerError:", sellerError);

          if (sellerError) {
            console.error("판매자 정보 로드 실패:", sellerError);
          } else if (sellerData) {
            setSeller(sellerData);
            console.log("5. seller 상태 설정됨:", sellerData); // 디버깅 로그
          } else {
            console.log("5. 해당 seller_id의 판매자 없음"); // 디버깅 로그
          }
        } else {
          console.log("3. seller_id 없음"); // 디버깅 로그
        }

        // 은행 정보 가져오기 (bank_id가 있을 경우)
        console.log("6. mappingData.bank_id:", mappingData.bank_id); // 디버깅 로그
        if (mappingData.bank_id) {
          const { data: bankData, error: bankError } = await supabase
            .from(JUNGNA_TABLES.banks)
            .select("*")
            .eq("id", mappingData.bank_id)
            .single();

          console.log("7. bankData:", bankData); // 디버깅 로그
          console.log("   bankError:", bankError);

          if (bankError) {
            console.error("은행 정보 로드 실패:", bankError);
          } else if (bankData) {
            console.log("8. bankInfo local state 설정됨:", bankData); // 디버깅 로그
          }
        } else {
          console.log("6. bank_id 없음"); // 디버깅 로그
        }

        // 조회수 증가 (mappingData.products가 null이 아님을 위에서 확인)
        const { error: updateError } = await supabase.rpc(
          JUNGNA_RPC.incrementProductViews,
          { product_id_input: mappingData.products.id }
        );

        if (updateError) {
          console.error("조회수 업데이트 실패:", updateError);
        }
      } catch (error) {
        console.error("데이터 로드 중 전체 오류:", error); // 디버깅 로그
        navigate("/404");
      } finally {
        setIsLoading(false);
        console.log("--- fetchData 종료 ---"); // 디버깅 로그
      }
    };

    fetchData();
  }, [url, navigate]); // 의존성 배열에서 setBankInfo 제거

  const handleOrderClick = () => {
    // product_id는 product 상태에서 가져와야 합니다.
    if (product && product.id) {
      navigate(`/product/${product.id}/order-payment`);
    } else {
      console.error("Product ID를 찾을 수 없습니다.");
      // 필요하다면 사용자에게 오류 메시지 표시
    }
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (!product) {
    return <div>상품을 찾을 수 없습니다.</div>;
  }

  // 판매자 정보를 SellerInfo 컴포넌트의 타입에 맞게 변환
  const sellerWithReviews = seller
    ? {
        name: seller.name,
        contact: seller.contact,
        reviews: {
          // null 병합 연산자 추가
          positive: seller.positive_reviews ?? 0,
          neutral: seller.neutral_reviews ?? 0,
          negative: seller.negative_reviews ?? 0,
        },
      }
    : null;

  console.log("--- 렌더링 직전 ---"); // 디버깅 로그
  console.log("seller 상태:", seller); // 디버깅 로그
  console.log("sellerWithReviews:", sellerWithReviews); // 디버깅 로그

  return (
    <Container>
      <ProductHeader title="중고나라" />
      <Content>
        <ImageCarousel images={images && images.length ? images : [product.image_url]} alt={product.title} />
        {/* sellerWithReviews가 null이 아닐 때만 SellerInfoWrapper 렌더링 */}
        {sellerWithReviews && <SellerInfo seller={sellerWithReviews} />}
        <ProductTitle>{product.title}</ProductTitle>
        <Price>{Number(product.price).toLocaleString()}원</Price>
        <DetailsRow>
          <DetailItem>{product.status}</DetailItem>
          <Separator>|</Separator>
          <DetailItem>{product.views} views</DetailItem>
          <Separator>|</Separator>
          <DetailItem>{product.date}</DetailItem>
        </DetailsRow>
        <Divider />
        {/* ProductContent 호출 시 htmlString prop 사용 */}
        <ProductContent htmlString={product.content} />
        <Divider />
        <OrderButton onClick={handleOrderClick}>주문하기</OrderButton>
      </Content>
    </Container>
  );
};

const Container = styled.main`
  max-width: 640px;
  margin: 0 auto;
  background-color: #fff;
  font-family: Inter, sans-serif;
  overflow-x: hidden;
  position: relative;
  padding-top: 64px;
  padding-bottom: 60px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  transform-origin: top left;
  @media (max-width: 640px) {
    transform: scale(0.8);
    width: 125%;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #fff;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #27c360;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ProductTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 16px;
`;

const Price = styled.p`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
`;

const DetailsRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const DetailItem = styled.p`
  font-size: 16px;
  margin-right: 8px;
`;

const Separator = styled.span`
  margin: 0 8px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ccc;
  margin: 16px 0;
`;

const OrderButton = styled.button`
  background-color: #27c360;
  color: #fff;
  border: none;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
`;

export default ProductDetails;
